import { z } from 'zod';
import { orgTypeSchema } from '../tenants/org-type';

/**
 * Zelfregistratie op uitnodiging (Stap 34, REG-1).
 *
 * HR maakt een **persoonlijke** uitnodiging (voor een persoon die al bestaat) of een **open**
 * (een link, QR of poster voor een groep nieuwe mensen, met een maximum). Wie de code heeft, vult
 * zijn eigen gegevens in, kiest een PIN en is meteen binnen. De uitnodiging ís de goedkeuring.
 *
 * Wat een nieuwe medewerker krijgt - rol, groepen, site - staat in de uitnodiging en nooit in
 * wat hij zelf instuurt.
 */

/** Zes cijfers, zoals bij activeren; de zwakke-PIN-regel toetst de server. */
const pinSchema = z.string().regex(/^\d{6}$/, 'Een pincode bestaat uit 6 cijfers.');

export const employeeInvitationKindSchema = z.enum(['person', 'open']);
export type EmployeeInvitationKind = z.infer<typeof employeeInvitationKindSchema>;

/** Per gegevensveld: optioneel vragen of verplicht. Wat ontbreekt, wordt niet gevraagd. */
export const fieldRequirementSchema = z.enum(['ask', 'required']);
export type FieldRequirement = z.infer<typeof fieldRequirementSchema>;

/**
 * De sleutel voor het personeelsnummer in `requiredFields`, naast de sleutels van de
 * gegevensvelden van het bedrijf. Wordt het niet gevraagd, dan krijgt de medewerker er een.
 */
export const EMPLOYEE_NUMBER_FIELD = 'employee_number';

export const invitationPrefillSchema = z.object({
  roleId: z.guid().nullable().default(null),
  groupIds: z.array(z.guid()).max(20).default([]),
  siteId: z.guid().nullable().default(null),
  /** Vrij label zoals "Uitzendkracht"; staat bij de persoon als gegevensveld als dat bestaat. */
  personType: z.string().max(40).nullable().default(null),
});
export type InvitationPrefill = z.infer<typeof invitationPrefillSchema>;

export const createEmployeeInvitationRequestSchema = z
  .object({
    kind: employeeInvitationKindSchema,
    /** "Instroom productie oktober": wat HR in de lijst leest. */
    name: z.string().trim().min(2, 'Geef de uitnodiging een naam.').max(120),
    /** Alleen bij `person`: de bestaande medewerker voor wie ze is. */
    personId: z.guid().nullable().default(null),
    // `prefault`: in zod 4 slaat `.default` het schema over, en dan ontbreken de standaardwaarden binnenin.
    prefill: invitationPrefillSchema.prefault({}),
    requiredFields: z.record(z.string(), fieldRequirementSchema).default({}),
    /** Standaard 30 dagen (Stap 34). */
    validDays: z.number().int().min(1).max(365).default(30),
    /** Alleen bij `open`; leeg = onbeperkt tot de vervaldatum. */
    maxUses: z.number().int().min(1).max(10000).nullable().default(null),
  })
  .refine((value) => (value.kind === 'person') === (value.personId !== null), {
    path: ['personId'],
    message: 'Een persoonlijke uitnodiging hoort bij één medewerker.',
  });
export type CreateEmployeeInvitationRequest = z.infer<typeof createEmployeeInvitationRequestSchema>;

export const employeeInvitationStatusSchema = z.enum(['open', 'expired', 'revoked', 'full']);
export type EmployeeInvitationStatus = z.infer<typeof employeeInvitationStatusSchema>;

export const employeeInvitationSchema = z.object({
  id: z.guid(),
  kind: employeeInvitationKindSchema,
  name: z.string(),
  status: employeeInvitationStatusSchema,
  /** `XXXX-XXXX`; alleen zolang de uitnodiging actief is (ADR-0075). */
  code: z.string().nullable(),
  /** De link die in de QR-code zit. */
  link: z.string().nullable(),
  personId: z.guid().nullable(),
  personName: z.string().nullable(),
  prefill: invitationPrefillSchema,
  /** "Productie · Ploeg B · Site A". */
  destination: z.string(),
  /** De naam van de rol in het Nederlands, of in de eerste taal die ingevuld is. */
  roleName: z.string().nullable(),
  /**
   * De naam van de rol per taalcode: het scherm toont die van de lezer (zie `Invitation.roleNames`).
   * `z.string()` als sleutel en niet `languageSchema`: dat staat in `auth/index.ts`, dat dit bestand
   * zelf exporteert - een import ervan liep in een kring (zie `tenants/org-type.ts`).
   */
  roleNames: z.record(z.string(), z.string()).nullable().default(null),
  requiredFields: z.record(z.string(), fieldRequirementSchema),
  maxUses: z.number().int().nullable(),
  usedCount: z.number().int(),
  expiresAt: z.string().datetime(),
  revokedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  invitedByName: z.string().nullable(),
});
export type EmployeeInvitation = z.infer<typeof employeeInvitationSchema>;

export const employeeInvitationListSchema = z.object({ items: z.array(employeeInvitationSchema) });
export type EmployeeInvitationList = z.infer<typeof employeeInvitationListSchema>;

export const invitationRegistrationSchema = z.object({
  personId: z.guid(),
  name: z.string(),
  at: z.string().datetime(),
});
export type InvitationRegistration = z.infer<typeof invitationRegistrationSchema>;

export const invitationRegistrationListSchema = z.object({
  items: z.array(invitationRegistrationSchema),
});
export type InvitationRegistrationList = z.infer<typeof invitationRegistrationListSchema>;

/**
 * Een registratie met een open code op een personeelsnummer dat al in de lijst stond (SEC-5).
 *
 * HR ziet naast elkaar wie er in de lijst staat en wat de aanvrager zelf intypte, en beslist of
 * het dezelfde persoon is. Pas na goedkeuring bestaat het account.
 */
export const linkRequestSchema = z.object({
  id: z.guid(),
  invitationId: z.guid(),
  invitationName: z.string(),
  personId: z.guid(),
  /** Zoals de medewerker in de lijst staat. */
  personName: z.string(),
  employeeNumber: z.string().nullable(),
  /** Wat de aanvrager zelf intypte. */
  typedName: z.string(),
  createdAt: z.string().datetime(),
});
export type LinkRequest = z.infer<typeof linkRequestSchema>;

export const linkRequestListSchema = z.object({ items: z.array(linkRequestSchema) });
export type LinkRequestList = z.infer<typeof linkRequestListSchema>;

// --- Publiek: wat de nieuwe medewerker ziet -------------------------------------------------

export const registrationFieldSchema = z.object({
  key: z.string(),
  /** Het label in de gevraagde taal, met terugval op Nederlands. */
  label: z.string(),
  /** `employee_number` is het personeelsnummer; de rest zijn de types van de gegevensvelden. */
  type: z.enum(['employee_number', 'text', 'number', 'date', 'select']),
  options: z.array(z.string()).nullable(),
  required: z.boolean(),
});
export type RegistrationField = z.infer<typeof registrationFieldSchema>;

/**
 * Wat een geldige code oplevert: **alleen** de naam en het logo van het bedrijf, de bestemming
 * als tekst en de gevraagde velden. Geen aantallen, geen namen (Stap 34).
 */
export const publicInvitationSchema = z.object({
  tenantName: z.string(),
  tenantLogoUrl: z.string().nullable(),
  /**
   * Het type organisatie (ORG-3): de registratieteksten volgen de woorden ervan ("je club", "je
   * lidnummer"). Optioneel voor een oudere server; zonder blijven het die van een fabriek.
   */
  orgType: orgTypeSchema.optional(),
  destination: z.string(),
  fields: z.array(registrationFieldSchema),
  language: z.string(),
  validUntil: z.string().datetime(),
});
export type PublicInvitation = z.infer<typeof publicInvitationSchema>;

export const registerRequestSchema = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  /** Sleutel → waarde, voor de velden uit `fields`. Velden die niet gevraagd werden, tellen niet. */
  fields: z.record(z.string(), z.string().max(200)).default({}),
  pin: pinSchema,
  language: z.string().min(2).max(5).default('nl'),
  /**
   * Genegeerd. Staat hier zodat een test kan aantonen dat een meegestuurde rol niets doet - de
   * rol komt uit de uitnodiging (REG-1).
   */
  roleId: z.string().optional(),
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

/**
 * Na registreren meteen aangemeld (zoals na activeren), plus wat hij later nodig heeft om weer
 * aan te melden: de bedrijfscode en zijn personeelsnummer.
 */
export const registerResponseSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int(),
  tokenType: z.literal('Bearer'),
  tenantCode: z.string(),
  employeeNumber: z.string(),
  firstName: z.string(),
});
export type RegisterResponse = z.infer<typeof registerResponseSchema>;
